import { useState, useEffect } from 'react';
import { doc, updateDoc, collection, query, where, getDocs, orderBy, Timestamp, addDoc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, History, Trash2, Save, Phone, Video, X, FileText, Edit, Target, Calendar, Plus, Users, Activity, LineChart } from 'lucide-react';
import { format, isToday } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useDebouncedCallback } from 'use-debounce';
import { FloatingVideoCall } from '@/components/video/FloatingVideoCall';
import { sendVideoCallNotification } from '@/lib/services/whatsapp';
import { AvailabilityService } from '@/lib/services/availabilityService';
// Removed generateVideoCallLink import - now using appointment ID directly
import { CoachExamDialog } from '@/components/coach/CoachExamDialog';
import { CoachHabitsDialog } from '@/components/coach/CoachHabitsDialog';
import { NextAppointmentDialog } from '@/components/coach/NextAppointmentDialog';
import { UnifiedQuestionnaireDialog } from '@/components/examiner/UnifiedQuestionnaireDialog';
import { hasCompletedQuestionnaire } from '@/lib/firebase/queries/userScores';
import { getCustomerExams } from '@/lib/firebase/queries/exams';
import { createAppointment } from '@/lib/firebase/queries/scheduling';
import { HealthMeasuresContent } from '@/components/customer/HealthMeasuresWidget';
import { ExamReportDialog } from '@/components/health/ExamReportDialog';
import type { Customer } from '@/components/examiner/types';

interface Appointment {
  id: string;
  customerId: string;
  customerName?: string;
  customerNameEn?: string;
  customerNameAr?: string;
  customerPhone?: string;
  date: Date;
  notes?: string;
  coachId: string;
  status: 'scheduled' | 'completed' | 'missed' | 'unscheduled';
}

interface NotesDialogProps {
  appointment: Appointment | null;
  onOpenChange: (open: boolean) => void;
  onNotesUpdate?: (id: string, notes: string) => void;
}

interface PreviousNote {
  id: string;
  date: Date;
  notes?: string;
  status: 'scheduled' | 'completed' | 'missed' | 'unscheduled';
}

export function NotesDialog({ appointment, onOpenChange, onNotesUpdate }: NotesDialogProps) {
  const [notes, setNotes] = useState('');
  const [originalNotes, setOriginalNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previousNotes, setPreviousNotes] = useState<PreviousNote[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [videoCallLinkId, setVideoCallLinkId] = useState<string | null>(null);
  const [callExists, setCallExists] = useState(false);
  const [whatsappSentToday, setWhatsappSentToday] = useState(false);
  const [createdAppointment, setCreatedAppointment] = useState<Appointment | null>(null);
  const [showCoachExam, setShowCoachExam] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showHabits, setShowHabits] = useState(false);
  const [showExamReport, setShowExamReport] = useState(false);
  const [showNextAppointment, setShowNextAppointment] = useState(false);
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false);
  const [hasExam, setHasExam] = useState(false);
  const [processingExam, setProcessingExam] = useState(false);
  const [hasAppointmentToday, setHasAppointmentToday] = useState(false);
  const [customerInWaitingRoom, setCustomerInWaitingRoom] = useState(false);
  const [waitingRoomPosition, setWaitingRoomPosition] = useState<number | null>(null);
  const { currentUser, userData } = useAuth();
  const { t, language, dir } = useLanguage();

  // Check if there are unsaved changes
  const hasChanges = notes !== originalNotes;
  
  // Check if this is a fake appointment from search (not a real appointment)
  const isFakeAppointment = appointment?.id.startsWith('search-');

  useEffect(() => {
    if (appointment) {
      const initialNotes = appointment.notes || '';
      setNotes(initialNotes);
      setOriginalNotes(initialNotes);
      setVideoCallLinkId(null);
      setCallExists(false);
      fetchPreviousNotes();
      checkQuestionnaireStatus();
      checkExamStatus();
      checkCallStatus();
      checkWhatsAppSentToday();
      checkAppointmentToday();
      
      // Listen for real-time customer presence updates
      const customerStatusRef = doc(db, 'calls', appointment.id, 'status', 'customer');
      const unsubscribe = onSnapshot(customerStatusRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const customerData = docSnapshot.data();
          const isCustomerOnline = customerData.joined || false;
          setCallExists(isCustomerOnline);
          console.log('👥 [NotesDialog] Customer presence updated:', {
            appointmentId: appointment.id,
            customerJoined: customerData.joined,
            isCustomerOnline,
            callExists: isCustomerOnline
          });
        } else {
          setCallExists(false);
          console.log('👥 [NotesDialog] No customer status document found, setting callExists to false');
        }
      });

      return () => unsubscribe();
    }
  }, [appointment]);

  // Subscribe to waiting room status for this customer
  useEffect(() => {
    if (!appointment?.customerId || !currentUser?.uid) {
      setCustomerInWaitingRoom(false);
      setWaitingRoomPosition(null);
      return;
    }

    const unsubscribe = AvailabilityService.listenToQueuePosition(
      currentUser.uid,
      appointment.customerId,
      (queueInfo) => {
        if (queueInfo) {
          setCustomerInWaitingRoom(true);
          setWaitingRoomPosition(queueInfo.position);
        } else {
          setCustomerInWaitingRoom(false);
          setWaitingRoomPosition(null);
        }
      }
    );

    return () => unsubscribe();
  }, [appointment?.customerId, currentUser?.uid]);

  const checkQuestionnaireStatus = async () => {
    if (!appointment?.customerId) return;
    
    try {
      const completed = await hasCompletedQuestionnaire(appointment.customerId);
      setQuestionnaireCompleted(completed);
    } catch (error) {
      console.error('Error checking questionnaire status:', error);
      setQuestionnaireCompleted(false);
    }
  };

  const checkExamStatus = async () => {
    if (!appointment?.customerId) return;
    
    try {
      const exams = await getCustomerExams(appointment.customerId);
      setHasExam(exams.length > 0);
    } catch (error) {
      console.error('Error checking exam status:', error);
      setHasExam(false);
    }
  };

  const checkWhatsAppSentToday = async () => {
    if (!appointment?.id) return;
    
    try {
      // Check if WhatsApp was sent today for this appointment
      const whatsappLogRef = doc(db, 'whatsapp_logs', appointment.id);
      const whatsappLogSnapshot = await getDoc(whatsappLogRef);
      
      if (whatsappLogSnapshot.exists()) {
        const logData = whatsappLogSnapshot.data();
        const lastSentDate = logData.lastSent?.toDate();
        const today = new Date();
        
        // Check if it was sent today
        const isSameDay = lastSentDate && 
          lastSentDate.getDate() === today.getDate() &&
          lastSentDate.getMonth() === today.getMonth() &&
          lastSentDate.getFullYear() === today.getFullYear();
        
        setWhatsappSentToday(isSameDay || false);
        console.log('📱 [NotesDialog] WhatsApp sent today check:', {
          appointmentId: appointment.id,
          lastSentDate: lastSentDate?.toISOString(),
          today: today.toISOString(),
          isSameDay,
          whatsappSentToday: isSameDay
        });
      } else {
        setWhatsappSentToday(false);
        console.log('📱 [NotesDialog] No WhatsApp log found, can send today');
      }
    } catch (error) {
      console.error('Error checking WhatsApp sent status:', error);
      setWhatsappSentToday(false);
    }
  };

  const checkAppointmentToday = async () => {
    if (!appointment?.customerId || !currentUser?.uid) return;
    
    try {
      // Get start and end of today
      const today = new Date();
      const startOfToday = new Date(today);
      startOfToday.setHours(0, 0, 0, 0);
      const endOfToday = new Date(today);
      endOfToday.setHours(23, 59, 59, 999);
      
      // Check if customer has any appointment today with this coach
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('customerId', '==', appointment.customerId),
        where('coachId', '==', currentUser.uid),
        where('date', '>=', Timestamp.fromDate(startOfToday)),
        where('date', '<=', Timestamp.fromDate(endOfToday))
      );
      
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      setHasAppointmentToday(!appointmentsSnapshot.empty);
      
      console.log('📅 [NotesDialog] Appointment today check:', {
        customerId: appointment.customerId,
        coachId: currentUser.uid,
        hasAppointmentToday: !appointmentsSnapshot.empty,
        appointmentsFound: appointmentsSnapshot.docs.length
      });
    } catch (error) {
      console.error('Error checking appointment today:', error);
      setHasAppointmentToday(false);
    }
  };

  const checkCallStatus = async () => {
    if (!appointment?.id) return;
    
    try {
      // Check if the other party (customer) is currently online in the call
      const customerStatusRef = doc(db, 'calls', appointment.id, 'status', 'customer');
      const customerStatusSnapshot = await getDoc(customerStatusRef);
      
      console.log('🔍 [NotesDialog] Checking call status for appointment:', appointment.id);
      
      if (customerStatusSnapshot.exists()) {
        const customerData = customerStatusSnapshot.data();
        const isCustomerOnline = customerData.joined || false;
        setCallExists(isCustomerOnline);
        console.log('👥 [NotesDialog] Customer status found:', {
          appointmentId: appointment.id,
          customerData,
          isCustomerOnline,
          callExists: isCustomerOnline
        });
      } else {
        setCallExists(false);
        console.log('👥 [NotesDialog] No customer status document found, setting callExists to false');
      }
    } catch (error) {
      console.error('Error checking call status:', error);
      setCallExists(false);
    }
  };

  const handleExamClick = async () => {
    if (!appointment?.customerId) return;
    
    try {
      setProcessingExam(true);
      // Just open the exam dialog - the dialog itself handles eligibility checking
      setShowCoachExam(true);
    } catch (error) {
      console.error('Error opening exam:', error);
      toast.error('Failed to open exam');
    } finally {
      setProcessingExam(false);
    }
  };

  const fetchPreviousNotes = async () => {
    if (!appointment) return;

    try {
      setLoadingHistory(true);
      const notesRef = collection(db, 'appointments');
      
      const q = query(
        notesRef,
        where('customerId', '==', appointment.customerId),
        where('date', '<', appointment.date),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      const notes = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate()
        }))
        .filter(note => note.notes && note.notes.trim() !== '') as PreviousNote[];

      setPreviousNotes(notes);
    } catch (error: any) {
      console.error('Error fetching previous notes:', error);
      if (error.code === 'failed-precondition') {
        toast.error('Please create the required index in Firebase Console');
        console.log('Required index: customerId (Ascending), date (Descending)');
      } else {
        toast.error('Failed to load notes history');
      }
      setPreviousNotes([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const debouncedSave = useDebouncedCallback(async (value: string) => {
    const currentAppointment = createdAppointment || appointment;
    if (!currentAppointment || isFakeAppointment) return;

    try {
      setIsSaving(true);
      await updateDoc(doc(db, 'appointments', currentAppointment.id), {
        notes: value,
        updatedAt: Timestamp.now()
      });
      onNotesUpdate?.(currentAppointment.id, value);
    } catch (error) {
      console.error('Error auto-saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  }, 1000);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    
    const currentAppointment = createdAppointment || appointment;
    if (canEditNotes && currentAppointment && !isFakeAppointment) {
      debouncedSave(value);
    }
  };

  const handleSaveAndClose = async () => {
    let currentAppointment = createdAppointment || appointment;
    
    // If this is a fake appointment with notes but no created appointment, create one first
    if (isFakeAppointment && !createdAppointment && notes.trim() && appointment) {
      try {
        setIsLoading(true);
        currentAppointment = await createUnscheduledAppointment(
          appointment.customerId,
          appointment.customerName,
          appointment.customerPhone
        );
        if (!currentAppointment) {
          onOpenChange(false);
          return;
        }
      } catch (error) {
        console.error('Error creating unscheduled appointment:', error);
        toast.error('Failed to create appointment');
        setIsLoading(false);
        return;
      }
    }
    
    if (!currentAppointment) {
      onOpenChange(false);
      return;
    }

    // If this is a fake appointment and no notes, just close
    if (isFakeAppointment && !notes.trim()) {
      onOpenChange(false);
      return;
    }

    // If no changes, just close
    if (!hasChanges && !isFakeAppointment) {
      onOpenChange(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Save the current notes state
      await updateDoc(doc(db, 'appointments', currentAppointment.id), {
        notes,
        updatedAt: Timestamp.now()
      });

      onNotesUpdate?.(currentAppointment.id, notes);
      toast.success('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsLoading(false);
      // Always close the dialog, regardless of save success/failure
      onOpenChange(false);
    }
  };

  const handleDelete = async () => {
    if (!appointment || isFakeAppointment) return;

    try {
      setIsLoading(true);
      await updateDoc(doc(db, 'appointments', appointment.id), {
        notes: '',
        updatedAt: Timestamp.now()
      });

      setNotes('');
      setOriginalNotes('');
      onNotesUpdate?.(appointment.id, '');
      toast.success('Notes deleted successfully');
    } catch (error) {
      console.error('Error deleting notes:', error);
      toast.error('Failed to delete notes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartVideoCall = async () => {
    if (!appointment) return;

    // Check if there's an appointment today first
    if (!hasAppointmentToday) {
      toast.error(language === 'ar' 
        ? 'يجب أن يكون هناك موعد اليوم لبدء المكالمة المرئية'
        : 'There must be an appointment today to start video call'
      );
      return;
    }

    // Check if appointment is today
    const appointmentDate = appointment.date instanceof Date ? appointment.date : appointment.date.toDate();
    if (!isToday(appointmentDate)) {
      toast.error(language === 'ar' 
        ? 'يمكن بدء المكالمات المرئية فقط في يوم الموعد'
        : 'Video calls can only be started on the appointment day'
      );
      return;
    }

    try {
      setSendingWhatsApp(true);
      
      // Use appointment ID as room ID (same as customer widget)
      setShowVideoCall(true);
      
      // Update call status after starting
      setTimeout(() => {
        checkCallStatus();
      }, 1000);
      
      // Get customer name based on language preference
      const customerName = language === 'ar' 
        ? (appointment.customerNameAr || appointment.customerNameEn || appointment.customerName)
        : (appointment.customerNameEn || appointment.customerName);

      // Only send WhatsApp notification if customer is not already online, phone number is available, and not sent today
      console.log('🔔 [NotesDialog] WhatsApp sending check:', {
        appointmentId: appointment.id,
        customerPhone: appointment.customerPhone,
        customerName: customerName,
        customerNameEn: appointment.customerNameEn,
        customerNameAr: appointment.customerNameAr,
        hasPhone: !!appointment.customerPhone,
        hasName: !!customerName,
        callExists,
        whatsappSentToday,
        shouldSend: !callExists && !whatsappSentToday,
        allConditionsMet: !!(appointment.customerPhone && customerName && !callExists && !whatsappSentToday)
      });
      
      if (appointment.customerPhone && customerName && !callExists && !whatsappSentToday) {
        console.log('✅ [NotesDialog] All conditions met, sending WhatsApp...');
        try {
          // Generate video call link using appointment ID directly
          const baseUrl = window.location.origin;
          const videoCallLink = `${baseUrl}/video-call/${appointment.id}`;

          console.log('📱 [NotesDialog] Sending WhatsApp with params:', {
            phone: appointment.customerPhone,
            name: customerName,
            link: videoCallLink,
            language
          });
      
      // Send WhatsApp notification with video call link
      const result = await sendVideoCallNotification(
        appointment.customerPhone,
            customerName,
        videoCallLink,
        language
      );

          console.log('📱 [NotesDialog] WhatsApp service result:', result);

      if (result.success) {
            console.log('✅ [NotesDialog] WhatsApp sent successfully');
            
            // Log that WhatsApp was sent today
            try {
              const whatsappLogRef = doc(db, 'whatsapp_logs', appointment.id);
              await setDoc(whatsappLogRef, {
                appointmentId: appointment.id,
                customerPhone: appointment.customerPhone,
                customerName: customerName,
                lastSent: new Date(),
                videoCallLink
              }, { merge: true });
              
              setWhatsappSentToday(true);
              console.log('📝 [NotesDialog] WhatsApp send logged successfully');
            } catch (logError) {
              console.error('Error logging WhatsApp send:', logError);
            }
            
        toast.success(t('dashboard.coach.whatsapp.notificationSent'));
          } else {
            console.error('❌ [NotesDialog] WhatsApp failed:', result.error);
            toast.error(`${t('dashboard.coach.whatsapp.notificationFailed')}: ${result.error}`);
          }
        } catch (whatsappError) {
          console.error('Error sending WhatsApp notification:', whatsappError);
          // Don't show error for WhatsApp failure, call still works
        }
      } else {
        console.log('❌ [NotesDialog] WhatsApp NOT sent because:', {
          missingPhone: !appointment.customerPhone,
          missingName: !customerName,
          callExists: callExists,
          whatsappSentToday: whatsappSentToday,
          reason: !appointment.customerPhone ? 'No phone number' : 
                  !customerName ? 'No customer name' : 
                  callExists ? 'Customer already online' : 
                  whatsappSentToday ? 'Already sent today' : 'Unknown reason'
        });
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      toast.error('Failed to start video call');
    } finally {
      setSendingWhatsApp(false);
    }
  };


  const canEditNotes = currentUser?.uid === (createdAppointment?.coachId || appointment?.coachId) || 
    (isFakeAppointment && currentUser?.uid); // Allow editing fake appointments to create unscheduled ones

  // Create customer object for exam dialog
  const customerForExam: Customer | null = appointment ? {
    id: appointment.customerId,
    firstNameEn: appointment.customerName?.split(' ')[0] || '',
    lastNameEn: appointment.customerName?.split(' ').slice(1).join(' ') || '',
    email: '',
    phone: appointment.customerPhone || ''
  } : null;

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-[90vw] w-[90vw] h-[90vh] max-h-[90vh]" 
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-4">
                <DialogTitle className="text-2xl">
                  {language === 'ar' 
                    ? (appointment.customerNameAr || appointment.customerNameEn || appointment.customerName || 'مستفيد غير معروف')
                    : (appointment.customerNameEn || appointment.customerName || 'Unknown Customer')
                  }
                </DialogTitle>
                {appointment.customerPhone && (
                  <a 
                    href={`tel:${appointment.customerPhone}`}
                    className="inline-flex items-center text-muted-foreground hover:text-primary"
                  >
                    <Phone className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {appointment.customerPhone}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-4">
                <DialogDescription className="text-base">
                  {isFakeAppointment ? 
                    format(new Date(), 'PPP') : 
                    format(appointment.date instanceof Date ? appointment.date : appointment.date.toDate(), 'PPP p')
                  }
                </DialogDescription>
                <Badge variant={
                  isFakeAppointment ? 'outline' :
                  appointment.status === 'completed' ? 'success' :
                  appointment.status === 'missed' ? 'destructive' :
                  appointment.status === 'unscheduled' ? 'outline' :
                  'secondary'
                }>
                  {isFakeAppointment ? 'unscheduled' : appointment.status}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-row-reverse">
              {canEditNotes && (
                <>
                  {/* 1. Close */}
                  <Button
                    size="sm"
                    onClick={handleSaveAndClose}
                    disabled={isLoading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                        {language === 'ar' ? 'جاري الحفظ والإغلاق...' : 'Saving & Closing...'}
                      </>
                    ) : (
                      <>
                        <Save className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                        {language === 'ar' ? 'حفظ وإغلاق' : 'Save & Close'}
                      </>
                    )}
                  </Button>
                  
                  
                  
                  {/* 4. Manage Habits */}
                  <Button
                    size="sm"
                    onClick={() => setShowHabits(true)}
                    className="bg-[#fbbf24] hover:bg-[#f59e0b] text-white border-[#fbbf24]"
                  >
                    <Target className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                    {language === 'ar' ? 'إدارة العادات' : 'Manage Habits'}
                  </Button>
                  
                  {/* 4. Record Exam */}
                  <Button
                    variant={hasExam ? "outline" : "default"}
                    size="sm"
                    onClick={handleExamClick}
                    disabled={processingExam}
                    className={hasExam ? "hover:bg-[#7dda55] hover:text-white" : "bg-[#7dda55] hover:bg-[#6dc447] text-white"}
                  >
                    {processingExam ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : hasExam ? (
                      <>
                        <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                        {language === 'ar' ? 'تعديل الفحص' : 'Edit Exam'}
                      </>
                    ) : (
                      <>
                        <FileText className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                        {language === 'ar' ? 'تسجيل الفحص' : 'Record Exam'}
                      </>
                    )}
                  </Button>
                  
                  {/* 5. Edit Questionnaire */}
                  <Button
                    variant={questionnaireCompleted ? "outline" : "default"}
                    size="sm"
                    onClick={() => setShowQuestionnaire(true)}
                    className={questionnaireCompleted ? "hover:bg-[#004250] hover:text-white" : "bg-[#004250] hover:bg-[#003240] text-white"}
                  >
                    {questionnaireCompleted ? (
                      <>
                        <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                        {language === 'ar' ? 'تعديل الاستبيان' : 'Edit Questionnaire'}
                      </>
                    ) : (
                      <>
                        <FileText className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                        {language === 'ar' ? 'ملء الاستبيان' : 'Fill Questionnaire'}
                      </>
                    )}
                  </Button>
                  
                  {/* 6. Next Appointment */}
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowNextAppointment(true)}
                    className="bg-purple-800 hover:bg-purple-900 text-white"
                  >
                    <Calendar className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                    {language === 'ar' ? 'الموعد التالي' : 'Next Appointment'}
                  </Button>
                  
                  {/* Delete Notes (conditional) */}
                  {notes && !isFakeAppointment && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {language === 'ar' ? 'حذف الملاحظات' : 'Delete Notes'}
                    </Button>
                  )}
                </>
              )}
              {!canEditNotes && (
                <Button
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500 hover:border-gray-600"
                >
                  <X className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {language === 'ar' ? 'إغلاق' : 'Close'}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 overflow-hidden h-[calc(90vh-120px)]">
          {/* Current Notes */}
          <div className="p-4 md:p-6 flex flex-col h-full">
            <div className="font-medium text-lg mb-4">
              {isFakeAppointment ? "Customer File" : "Today Notes"}
            </div>
            <Textarea
              placeholder={
                isFakeAppointment 
                  ? "Enter notes for this customer (appointment will be created when saved)..." 
                  : canEditNotes 
                    ? "Enter your notes for this appointment..." 
                    : "No notes available for this appointment"
              }
              className="flex-1 min-h-0 resize-none text-base p-4"
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              disabled={isLoading || !canEditNotes}
            />
            
            {/* Waiting Room Status */}
            {customerInWaitingRoom && (
              <div className="mt-4 p-4 bg-[#004250]/5 border border-[#004250]/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[#004250] rounded-full animate-pulse"></div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#004250]" />
                    <span className="text-sm font-medium text-[#004250]">
                      {language === 'ar' 
                        ? `المستفيد في غرفة الإنتظار (المركز ${waitingRoomPosition})`
                        : `Customer in waiting room (Position ${waitingRoomPosition})`
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Previous Notes */}
          <div className="p-4 md:p-6 flex flex-col h-full overflow-hidden">
            {/* Health Measures */}
            {appointment?.customerId && (
              <div className="mb-6 space-y-4">
                <div className="font-medium text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Health Measures
                  <Button
                    variant="outline"
                    size="sm"
                    className="ms-auto"
                    onClick={() => setShowExamReport(true)}
                    aria-label={t('common.health.report.openFor')}
                    data-testid="notes-exam-report-button"
                  >
                    <LineChart className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} aria-hidden />
                    {t('common.health.report.button')}
                  </Button>
                </div>
                <div className="bg-muted border border-border rounded-lg p-4">
                  <HealthMeasuresContent customerId={appointment.customerId} forceEnglish={true} />
                </div>
              </div>
            )}
            
            <div className="font-medium text-lg flex items-center gap-2 mb-4">
              <History className="h-5 w-5" />
              Previous Notes
            </div>
            <div className="flex-1 min-h-0">
              <div className="h-full flex flex-col">
                <div className="flex-1 bg-muted border border-border rounded-lg p-4 overflow-y-auto">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : previousNotes.length > 0 ? (
                    <div className="space-y-6">
                      {previousNotes.map((note) => (
                        <div key={note.id} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">
                              {format(note.date, 'PPP p')}
                            </div>
                            <Badge variant={
                              note.status === 'completed' ? 'success' :
                              note.status === 'missed' ? 'destructive' :
                              note.status === 'unscheduled' ? 'outline' :
                              'secondary'
                            }>
                              {note.status}
                            </Badge>
                          </div>
                          <div className="whitespace-pre-wrap text-sm">
                            {note.notes || 'No notes recorded for this appointment'}
                          </div>
                          <Separator />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <History className="h-12 w-12 mb-4 opacity-50" />
                      <p>No previous notes found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <FloatingVideoCall
          roomId={appointment?.id || ''}
          userRole="coach"
          userName={currentUser?.displayName || 'Coach'}
          otherParticipantName={
            language === 'ar' 
              ? (appointment?.customerNameAr || appointment?.customerNameEn || appointment?.customerName)
              : (appointment?.customerNameEn || appointment?.customerName)
          }
          onCallEnded={() => setShowVideoCall(false)}
          open={showVideoCall && !!appointment}
          appointmentDate={appointment?.date instanceof Date ? appointment.date : appointment?.date?.toDate()}
        />
      </DialogContent>

      {/* Coach Exam Dialog */}
      {showCoachExam && customerForExam && (
        <CoachExamDialog
          customer={customerForExam}
          open={showCoachExam}
          onOpenChange={setShowCoachExam}
          onSuccess={() => {
            setShowCoachExam(false);
            checkExamStatus(); // Refresh exam status
            toast.success('Examination completed successfully');
          }}
        />
      )}

      {/* Coach Habits Dialog */}
      {showHabits && appointment && (
        <CoachHabitsDialog
          open={showHabits}
          onOpenChange={setShowHabits}
          customerId={appointment.customerId}
          customerName={appointment.customerName || appointment.customerNameEn || 'Unknown Customer'}
          customerNameAr={appointment.customerNameAr}
        />
      )}

      {/* Exam report over time (#112) */}
      {showExamReport && appointment?.customerId && (
        <ExamReportDialog
          open={showExamReport}
          onOpenChange={setShowExamReport}
          customerId={appointment.customerId}
          customerName={
            language === 'ar'
              ? appointment.customerNameAr || appointment.customerNameEn || appointment.customerName
              : appointment.customerNameEn || appointment.customerName
          }
        />
      )}

      {/* Questionnaire Dialog */}
      {showQuestionnaire && appointment && (
        <UnifiedQuestionnaireDialog
          open={showQuestionnaire}
          onOpenChange={setShowQuestionnaire}
          customerId={appointment.customerId}
          customerName={appointment.customerName || 'Unknown Customer'}
          onSuccess={() => {
            setShowQuestionnaire(false);
            checkQuestionnaireStatus(); // Refresh questionnaire status
            toast.success('Questionnaire completed successfully');
          }}
        />
      )}

      {/* Next Appointment Dialog */}
      {showNextAppointment && appointment && (
        <NextAppointmentDialog
          open={showNextAppointment}
          onOpenChange={setShowNextAppointment}
          customerId={appointment.customerId}
          customerName={appointment.customerName || appointment.customerNameEn || 'Unknown Customer'}
          customerNameAr={appointment.customerNameAr}
          customerPhone={appointment.customerPhone}
        />
      )}
    </Dialog>
  );
}